import { useEffect, useMemo, useState } from 'react';
import { Badge, Box, Button, Flex, Icon, Spinner, Text } from '@chakra-ui/react';
import { LockClosedIcon, PencilIcon, ShareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { PlanItem, PlanScope } from '@/lib/types';
import { draftFromPlan } from '@/pages/planner/draft';
import { PlannerAction } from '@/pages/planner/planner-machine';
import usePlannerPlansStore from '@/utils/use-planner-plans';
import { buildPublicUrl } from '@/utils/public-url';
import useIsViewer from '@/lib/hooks/use-is-viewer';
import { toaster } from '@/components/ui/toaster';
import PrintButton from '@/components/ui/print-button';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import { useModalValueToggle } from '@/lib/hooks/use-modal-toggle';
import PlannerItemDrawer from '@/pages/planner/item-drawer/planner-item-drawer';
import PlanMetaChips from './plan-meta-chips';
import PlanBody from './plan-body';
import '@/styles/print.css';

interface ViewScreenProps {
  date: string;
  scope: PlanScope;
  dispatch: (action: PlannerAction) => void;
}

// Read-only "view" screen for one of a day's plans (`scope` picks which —
// the workspace plan or the caller's "My Plan"; the tab strip above switches
// between them): date badge, title, "From curriculum" chip, meta chips, the
// shared plan body (plan-body.tsx — outcome boxes, Goals/Notes, sections
// with computed per-section start times), and Share/Print/Edit/Delete
// actions. The content-item detail slide-over is shared with the builder
// (planner-item-drawer.tsx).
export default function ViewScreen({ date, scope, dispatch }: ViewScreenProps) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const fetchByDate = usePlannerPlansStore((s) => s.fetchByDate);
  const items = usePlannerPlansStore((s) => s.items);
  const deletePlan = usePlannerPlansStore((s) => s.deletePlan);
  const isViewer = useIsViewer();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const itemDrawer = useModalValueToggle<PlanItem>();

  // Read straight from the store (not a local snapshot) so tab switches and
  // the delete flow below stay in sync with the scope tab strip, which
  // renders from the same items.
  const dayPlans = useMemo(
    () => items.filter((item) => item.plan_date.substring(0, 10) === date),
    [items, date],
  );
  const plan = dayPlans.find((item) => item.scope === scope);

  // Starts true when the date has nothing cached at mount, so the fetch
  // effect's first pass renders a spinner rather than flashing "No plan
  // found".
  const [isLoading, setIsLoading] = useState(() => dayPlans.length === 0);

  useEffect(() => {
    // Fall back to a direct fetch (which merges into the store) when the date
    // holds nothing yet — a deep link outside the loaded calendar range, or a
    // range boundary edge case. A date that has plans but not this scope is a
    // genuinely empty slot: no fetch needed.
    if (dayPlans.length > 0 || !workspaceId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchByDate(workspaceId, date)
      .catch((error: any) => {
        toaster.create({
          type: 'error',
          title: error.response?.data?.error ?? 'Failed to load practice plans',
        });
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, workspaceId]);

  // Copies the public share link (/public/planner/:referenceId) — viewable
  // without logging in, unlike the workspace planner URL.
  const handleShare = async () => {
    if (!plan) return;
    const url = buildPublicUrl('planner', plan.reference_id);
    try {
      await navigator.clipboard.writeText(url);
      toaster.create({ type: 'success', title: 'Link copied to clipboard' });
    } catch {
      toaster.create({ type: 'error', title: 'Failed to copy link' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!plan) return;
    setIsDeleting(true);
    // Resolved before the delete mutates the store: where to land afterwards —
    // the date's other plan when one exists, otherwise the (scope-preset)
    // choice screen.
    const otherPlan = dayPlans.find((item) => item.scope !== scope);
    try {
      await deletePlan(plan.id);
      toaster.create({ type: 'success', title: 'Practice plan deleted' });
      setShowDeleteConfirm(false);
      dispatch({
        type: 'GO_TO',
        screen: otherPlan
          ? { name: 'view', date, scope: otherPlan.scope }
          : { name: 'choice', date, scope },
      });
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: error.response?.data?.error ?? 'Failed to delete practice plan',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Flex justify='center' py={10}>
        <Spinner color='fg.subtle' />
      </Flex>
    );
  }

  if (!plan) {
    return (
      <Text fontSize='sm' color='fg.muted'>
        No plan found for this date.
      </Text>
    );
  }

  const formattedDate = format(new Date(`${plan.plan_date}T00:00:00`), 'EEE MMM d');
  // A workspace plan the caller may not edit (admin-authored, contributor
  // viewing) gets the explicit read-only treatment from the prototype: a
  // disabled lock button in the actions row plus the banner below the header.
  const isReadOnlyForContributor = !isViewer && !plan.can_edit;

  return (
    // Drop data-print-target while the item drawer is open: the drawer
    // renders its own [data-print-target] in a body-level Portal, and
    // ViewContent's print flow grabs the first DOM match — without this,
    // printing from the drawer would clone this box's content instead.
    <Box {...(itemDrawer.isOpen ? {} : { 'data-print-target': true })}>
      <Flex align='center' justify='space-between' mb={4} flexWrap='wrap' gap={3}>
        <Box>
          <Flex align='center' gap={2} mb={1}>
            <Badge colorPalette='brand' size='sm' textTransform='uppercase'>
              {formattedDate}
            </Badge>
            {plan.from_curriculum && (
              <Badge variant='outline' size='sm'>
                From curriculum
              </Badge>
            )}
          </Flex>
          <Text fontFamily='heading' fontWeight='800' fontSize='h2' textTransform='uppercase' color='fg.strong'>
            {plan.name}
          </Text>
        </Box>

        <Flex gap={2} flexWrap='wrap' className='no-print'>
          <Button variant='outline' onClick={handleShare}>
            <Icon as={ShareIcon} w={4} h={4} />
            Share
          </Button>
          <PrintButton documentTitle={plan.name} />
          {/* Viewers are read-only: Edit/Delete are write actions and stay
              hidden for them. Share/Print remain available above.
              can_edit additionally locks the plan for a contributor looking
              at one an admin authored — admin-authored plans are admin-only,
              and the API refuses the write either way. */}
          {!isViewer && plan.can_edit && (
            <>
              <Button
                variant='outline'
                onClick={() =>
                  dispatch({ type: 'EDIT_DAY_PLAN', date: plan.plan_date, draft: draftFromPlan(plan) })
                }
              >
                <Icon as={PencilIcon} w={4} h={4} />
                Edit
              </Button>
              <Button variant='danger' onClick={() => setShowDeleteConfirm(true)}>
                <Icon as={TrashIcon} w={4} h={4} />
                Delete
              </Button>
            </>
          )}
          {isReadOnlyForContributor && (
            <Button variant='outline' disabled>
              <Icon as={LockClosedIcon} w={4} h={4} />
              Read-only
            </Button>
          )}
        </Flex>
      </Flex>

      {isReadOnlyForContributor && (
        <Flex
          align='center'
          gap={2.5}
          bg='slot4.bg'
          borderWidth='1px'
          borderColor='slot4.fg'
          borderRadius='md'
          px={4}
          py={3}
          mb={5}
        >
          <Icon as={LockClosedIcon} w={4} h={4} color='slot4.fg' flexShrink={0} />
          <Text fontSize='sm' color='fg.strong'>
            This plan was published by your workspace admin and cannot be edited.
          </Text>
        </Flex>
      )}

      <Box mb={5}>
        <PlanMetaChips startTime={plan.start_time} durationMinutes={plan.duration_minutes} />
      </Box>

      <PlanBody plan={plan} startTime={plan.start_time} onViewItem={itemDrawer.open} />

      <DestructiveModal
        isOpen={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title='Delete practice plan'
        confirmText='Delete'
      >
        <Text fontSize='sm' color='fg.muted'>
          Are you sure you want to delete <b>{plan.name}</b>? This cannot be undone.
        </Text>
      </DestructiveModal>

      <PlannerItemDrawer isOpen={itemDrawer.isOpen} onClose={itemDrawer.close} item={itemDrawer.state} />
    </Box>
  );
}
